import { axios } from "@/utils"

/**
 * Authentication Service
 * Centralized API client for all authentication-related endpoints
 */

const AuthService = {
    /**
     * PASSWORD AUTH
     * Email/password login
     * @param {string} username - Email address
     * @param {string} password - Password
     * @returns {Promise<{token: string}>}
     */
    getAuthToken(username, password) {
        return axios.post('/api/v1/get-token-auth/', { username, password })
    },

    /**
     * EMAIL TOKEN AUTH (Passwordless)
     * Request login - checks if user exists and sends token
     * @param {string} email - Email address
     * @returns {Promise<{status: string, email: string}>}
     * Response: { status: "token_sent"|"not_found", email: "..." }
     */
    requestLogin(email) {
        return axios.post('/api/v2/www/auth/request-login/', { email })
    },

    /**
     * EMAIL TOKEN AUTH (Passwordless)
     * Register new user and send token
     * @param {Object} payload - Registration data
     * @param {string} payload.email - Email address
     * @param {string} payload.language - User language (e.g., 'it', 'en')
     * @param {string} payload.first_name - First name
     * @param {string} payload.last_name - Last name
     * @param {boolean} payload.consent_privacy_and_terms - Privacy consent
     * @param {boolean} payload.consent_3rd_party_marketing - Marketing consent
     * @returns {Promise<{status: string, email: string}>}
     * Response: { status: "registration_token_sent", email: "..." }
     */
    registerUser(payload) {
        return axios.post('/api/v2/www/auth/register/', {
            email: payload.email,
            language: payload.language,
            first_name: payload.first_name || '',
            last_name: payload.last_name || '',
            consent_privacy_and_terms: payload.consent_privacy_and_terms,
            consent_3rd_party_marketing: payload.consent_3rd_party_marketing || false
        })
    },

    /**
     * EMAIL TOKEN AUTH (Passwordless)
     * Verify email token and authenticate
     * @param {string} email - Email address
     * @param {string} token - 4-digit verification code
     * @param {Object} options - Optional query params
     * @param {boolean} options.include_perms - Include permissions in response
     * @param {boolean} options.include_groups - Include groups in response
     * @param {boolean} options.include_jwt - Include JWT in response
     * @returns {Promise<{token: string, user_id: number, email: string}>}
     */
    verifyToken(email, token, options = {}) {
        const params = new URLSearchParams()
        if (options.include_perms) params.append('include_perms', '1')
        if (options.include_groups) params.append('include_groups', '1')
        if (options.include_jwt) params.append('include_jwt', '1')

        const queryString = params.toString()
        const url = queryString
            ? `/api/v2/www/auth/verify-token/?${queryString}`
            : '/api/v2/www/auth/verify-token/'

        return axios.post(url, { email, token })
    },

    /**
     * MAGIC LINK AUTH (Passwordless)
     * Request magic link - checks if user exists and sends magic link email
     * @param {string} email - Email address
     * @returns {Promise<{status: string, email: string}>}
     * Response: { status: "magic_link_sent"|"not_found", email: "..." }
     */
    requestMagicLink(email) {
        return axios.post('/api/v2/www/auth/request-magic-link/', { email })
    },

    /**
     * MAGIC LINK AUTH (Passwordless)
     * Register new user and send magic link
     * @param {Object} payload - Registration data
     * @param {string} payload.email - Email address
     * @param {string} payload.language - User language (e.g., 'it', 'en')
     * @param {string} payload.first_name - First name
     * @param {string} payload.last_name - Last name
     * @param {boolean} payload.consent_privacy_and_terms - Privacy consent
     * @param {boolean} payload.consent_3rd_party_marketing - Marketing consent
     * @returns {Promise<{status: string, email: string}>}
     * Response: { status: "registration_magic_link_sent", email: "..." }
     */
    registerUserMagicLink(payload) {
        return axios.post('/api/v2/www/auth/register-magic-link/', {
            email: payload.email,
            language: payload.language,
            first_name: payload.first_name || '',
            last_name: payload.last_name || '',
            consent_privacy_and_terms: payload.consent_privacy_and_terms,
            consent_3rd_party_marketing: payload.consent_3rd_party_marketing || false
        })
    },

    /**
     * MAGIC LINK AUTH (Passwordless)
     * Verify magic link code and authenticate
     * Called when user clicks link in email with ?magic_link=CODE&email=xxx
     * @param {string} email - User email
     * @param {string} code - Magic link code from URL (8 digits)
     * @returns {Promise<{token: string, user_id: number, email: string}>}
     */
    verifyMagicLink(email, code) {
        return axios.post('/api/v2/www/auth/verify-magic-link/', { email, code })
    },

    /**
     * SOCIAL LOGIN (Google, Facebook, Apple)
     * Convert OAuth token to application token
     * @param {Object} payload
     * @param {string} payload.backend - 'google-oauth2' | 'facebook' | 'apple-id'
     * @param {string} payload.token - OAuth access token
     * @param {string} payload.grant_type - 'convert_token'
     * @param {string} payload.client_id - OAuth client ID
     * @param {string} [payload.language] - User language
     * @returns {Promise<{access_token: string, token_type: string}>}
     */
    convertAuthToken(payload) {
        return axios.post('/auth/convert-token/', payload)
    },

    /**
     * SSO LOGIN
     * Single Sign-On integration
     * @param {string} integration_id - SSO integration identifier
     * @param {string} token - SSO token
     * @returns {Promise<{token: string}>}
     */
    getTokenFromSsoLogin(integration_id, token) {
        return axios.post('/v2/user/sso-integration/', {
            token: token,
            integration: integration_id
        })
    },

    /**
     * IBI TOKEN LOGIN (Special integration)
     * @param {Object} payload
     * @returns {Promise<{access_token: string, token_type: string}>}
     */
    convertIbiAuthToken(payload) {
        return axios.post('/accounts/ibi/convert-token/', payload)
    },

    /**
     * USER DATA
     * Get current authenticated user data
     * @returns {Promise<Object>} User object with permissions
     */
    getUserData() {
        return axios.get(`/v2/users/me/?include_perms=true`)
    },

    /**
     * USER REGISTRATION
     * Create new user account
     * @param {Object} payload - Registration data
     * @returns {Promise<Object>}
     */
    createAccount(payload) {
        return axios.post(`/v2/users/create/`, payload)
    },

    /**
     * USER UPDATE
     * Update user profile
     * @param {Object} payload - User data to update
     * @returns {Promise<Object>}
     */
    updateUser(payload) {
        return axios.patch(`/v2/users/me/`, payload)
    },

    /**
     * PASSWORD RESET
     * Request password reset email
     * @param {Object} payload - {email}
     * @returns {Promise<void>}
     */
    resetPassword(payload) {
        return axios.post(`/api/v1/password/reset/`, payload)
    },

    /**
     * PASSWORD CHANGE
     * Change user password (requires old password)
     * @param {string} oldPassword
     * @param {string} newPassword1
     * @param {string} newPassword2
     * @returns {Promise<void>}
     */
    changePassword(oldPassword, newPassword1, newPassword2) {
        return axios.post(`/api/v1/password/change/`, {
            'old_password': oldPassword,
            'new_password1': newPassword1,
            'new_password2': newPassword2
        })
    }
}

export default AuthService
